import React, { useEffect, useMemo, useState } from 'react';
import cls from 'classnames';
import Swiper from '@/pages/intelligence/components/Swiper';
import Loading from '@/pages/intelligence/components/Loading';
import DownloadableContent from '@intelligence/pages/TopCharts/components/DetailsPage/DownloadableContent';
import { swiperParamsInfo } from '@/pages/intelligence/pages/_config/topchartsInterface';
import GameInfoTagCard from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/GameInfoTagCard';
import LanguagesInfoCard from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/LanguagesInfoCard';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import useFilterTagsOptions from '@intelligence/hooks/useFilterTagsOptions';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import { IOpinionLang } from '@intelligence/pages/TopCharts/Companies/components/GamesDetails/NewsActivity/MediaNews/types';
import ReleaseDate, {
  IReleaseDateByPlatformData,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/ReleaseDate';
import JoinXboxGamePassDateCard from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/JoinXboxGamePassDateCard';
import igdbIcon from '@intelligence/assets/svg/gameInfo/igdb.svg';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import {
  DownloadContentLocaleMap,
  noDataTipLocaleMap,
  SegmentSortMap,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/AboutGameContent/_config';

import './index.scss';

interface IAboutGameContent {
  isRelease: boolean;
  platform: string;
  images: string[];
  videos: string[];
  swiperParams: swiperParamsInfo;
  desc: { origin?: string; zh?: string; en?: string };
  videoScreen: string[];
  steamTags: string[];
  gameModeTags: string[];
  gameEngineTags?: string[];
  languageInfo: { [key: string]: {} };
  dLoading: boolean;
  entityType: string;
  downloadContent: { [key: string]: any };
  releaseDateByPlatforms: IReleaseDateByPlatformData[];
}

const AboutGameContent: React.FC<IAboutGameContent> = (props: any) => {
  const {
    isRelease,
    platform,
    images,
    videos,
    swiperParams,
    desc,
    videoScreen,
    dLoading,
    entityType,
    steamTags,
    gameModeTags,
    gameEngineTags = [],
    languageInfo,
    downloadContent,
    releaseDateByPlatforms,
  } = props;

  const { formatMessage, locale, isCN } = useIntlExtend();

  // 当前描述显示语种
  const [descLang, setDescLang] = useState(isCN ? 'zh' : 'en');
  // 根据当前语言更新选中的语言框
  useEffect(() => {
    setDescLang(isCN ? 'zh' : 'en');
  }, [isCN]);

  // 当 screenshots 轮播内容为五个或以上，调窄 flex 右侧元素宽度，避免上下对不齐
  const showRightMinPercent = images?.length + videos?.length >= 5;

  const hasSteamPlatform = platform?.includes('steam');
  // 有发 Steam 平台 或者有 tag 数据时才展示两个卡片
  const showSteamTags = Boolean(hasSteamPlatform || steamTags?.length || gameModeTags?.length);

  // TODO_NOTE: 指标不明确，需求待讨论放下个迭代
  const showXboxJoinedDate = false;

  // 当前语言 tags 文案映射
  const { gameModeLocalTextMap, steamTagsLocalTextMap, gameEngineLocaleTextMap } = useFilterTagsOptions();

  const noDataTip = useMemo(() => {
    const emptyTipsLocaleKey = isRelease
      ? 'dbi_detail_common_nodata_tips'
      : 'dbi_detail_game_not_launch_tips';
    return formatMessage({ id: emptyTipsLocaleKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, isRelease]);

  const descNoDataTip = useMemo(() => {
    const emptyTipsLocaleKey = isRelease
      ? 'dbi_detail_description_nodata_tips'
      : 'dbi_detail_game_not_launch_tips';

    // 描述信息空态文案和语言选择框联动
    if (isRelease) {
      return noDataTipLocaleMap[descLang];
    }

    return formatMessage({ id: emptyTipsLocaleKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, descLang, isRelease]);

  // 相关内容（下载内容组件）segment 根据相关指标顺序排序
  const downloadContentSegmentSort = (arr: { value: string }[]) => {
    if (!arr?.length) return [];

    return arr.sort((a, b) => {
      const aValue = isCN ? a.value?.split('|')?.[1] : a.value;
      const bValue = isCN ? b.value?.split('|')?.[1] : b.value;

      const aValueSort = SegmentSortMap[aValue];
      const bValueSort = SegmentSortMap[bValue];

      if (aValueSort && bValueSort) {
        return aValueSort - bValueSort;
      }
      if (aValueSort) {
        return -1;
      }
      if (bValueSort) {
        return 1;
      }
      return 0;
    });
  };

  // DownloadContent 中文时键名需拼接处理
  const localeDownloadContent = useMemo(() => {
    return isCN
      ? Object.keys(downloadContent)?.reduce((prev, curr) => {
          // 中文时键名使用 'zh|en' 拼接，避免纯中文名重复问题
          const key = `${DownloadContentLocaleMap[curr] || curr}|${curr}`;
          const list = downloadContent[curr];
          return { ...prev, [key]: list };
        }, {})
      : downloadContent;
  }, [isCN, downloadContent]);

  // 当前选中语言对应描述文案
  const currentLocaleDesc = useMemo(() => {
    return desc[descLang];
  }, [desc, descLang]);

  return (
    <div className="dbi-game-info-about-content">
      {dLoading && (
        <div
          style={{
            height: 200,
            color: '#444',
            background: '#fff',
            borderRadius: 8,
          }}
        >
          <Loading />
        </div>
      )}

      {!dLoading && (
        <>
          <div className="dbi-about-flex">
            {/* 左侧内容 */}
            <div className={'dbi-about-flex-left dbi-about-flex-col'}>
              {/* Description 描述*/}
              {/* TODO_NOTE: remove hide-joined-xbox-time 高度调整 */}
              <div className="text-box dbi-about-flex-col-auto hide-joined-xbox-time">
                <div className="dbi-about-title-container">
                  <div className="dbi-about-title">{formatMessage({ id: `dbi_description` })}</div>

                  <SingleTranslateSelect
                    defaultValue={descLang}
                    style={{ marginRight: 12 }}
                    onChange={(_key: string, value: IOpinionLang) => {
                      setDescLang(value);
                    }}
                  />
                </div>
                <div className="text-box-context">
                  {currentLocaleDesc ? (
                    <div
                      className="intelligence-desc dbi-intelligence-desc"
                      dangerouslySetInnerHTML={{ __html: currentLocaleDesc }}
                    ></div>
                  ) : (
                    <div className="nodata-center-des">{descNoDataTip}</div>
                  )}
                </div>
              </div>
            </div>

            {/* 右侧内容 */}
            <div
              className={cls('dbi-about-flex-right dbi-about-flex-combine-height', {
                'right-min-percent': showRightMinPercent,
              })}
            >
              {/* Release Date 正式上线时间 */}
              <ReleaseDate isRelease={isRelease} list={releaseDateByPlatforms} />
            </div>
          </div>

          <div className="dbi-about-flex">
            {/* 左侧内容 */}
            <div className={'dbi-about-flex-left dbi-about-flex-col'}>
              {/* Screenshots 截屏 */}
              <div className="dbi-video-box">
                <div className="dbi-about-title">{formatMessage({ id: `dbi_screenshots` })}</div>
                <Swiper
                  urlList={images}
                  videoScreen={videoScreen}
                  videos={videos}
                  noDataTip={noDataTip}
                  params={swiperParams}
                  height={'calc(100% - 31px)'}
                />
              </div>
            </div>

            {/* 右侧内容 */}
            <div
              className={cls('dbi-about-flex-right dbi-about-flex-combine-height', {
                'right-min-percent': showRightMinPercent,
              })}
            >
              {/* 加入 Xbox Game Pass 的时间 */}
              {showXboxJoinedDate && <JoinXboxGamePassDateCard time={'TODO_NOTE:time'} />}

              {/* Date joined Xbox Game Pass Steam Tags Steam 标签 */}
              {showSteamTags && (
                <GameInfoTagCard
                  type={filterKeyMap.STEAM_TAGS}
                  title={formatMessage({ id: 'dbi_top_steam_tags' })}
                  tagList={steamTags}
                  localeTextMap={steamTagsLocalTextMap}
                />
              )}

              {/* Game Mode 游戏模式 */}
              {showSteamTags && (
                <GameInfoTagCard
                  type={filterKeyMap.GAME_MODE}
                  title={formatMessage({ id: 'dbi_top_game_mode' })}
                  tagList={gameModeTags}
                  localeTextMap={gameModeLocalTextMap}
                />
              )}

              {/* Game Engine 游戏引擎 */}
              <GameInfoTagCard
                type={filterKeyMap.GAME_ENGINE}
                title={formatMessage({ id: 'dbi_top_game_engine' })}
                tagList={gameEngineTags}
                localeTextMap={gameEngineLocaleTextMap}
                entityType={entityType}
                compact={true}
                style={{ marginBottom: 20 }}
              />

              {/* Game Language 游戏语言 - moved here to fill remaining space */}
              <LanguagesInfoCard data={languageInfo} />
            </div>
          </div>

          <div className="dbi-about-flex">
            {/* 左侧内容 */}
            <div className="dbi-about-flex-left">
              {/* Related Content 相关内容*/}
              <div
                className={cls('dbi-about-related-content-box', {
                  'no-related-data': !Object.keys(localeDownloadContent || {})?.length,
                })}
                // style={{ maxHeight: relatedContentMaxHeight }}
              >
                <div
                  className={cls('dbi-about-title', {
                    'related-content-no-segment':
                      Object.keys(localeDownloadContent || {})?.length <= 1,
                  })}
                >
                  <img className="dbi-about-title-icon" src={igdbIcon} />
                  {formatMessage({ id: `dbi_related_content` })}
                </div>
                <DownloadableContent
                  isRelease={isRelease}
                  entityType={entityType}
                  downloadContent={localeDownloadContent}
                  segmentsSortFn={downloadContentSegmentSort}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AboutGameContent;
